import {useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {resetPassword} from '../core/_requests'
import Swal from 'sweetalert2'
import {DEFAULT_SUCCESS_MODAL, DEFAULT_ERROR_MODAL} from '../../../constants/errors/error-model'

const initialValues = {
  password: '',
  confirmPassword: '',
}
const strongPasswordMsg =
  'Must contain at least 6 Characters, 1 Uppercase, 1 Lowercase, 1 Special Character, and 1 Number'
const strongPasswordValidate =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*[\]{}()?"\\/,><':;|_~`=+-])[a-zA-Z\d!@#$%^&*[\]{}()?"\\/,><':;|_~`=+-]{6,}$/

const forgotPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .matches(strongPasswordValidate, strongPasswordMsg),
  confirmPassword: Yup.string()
    .required('Confirm Password is required')
    .oneOf([Yup.ref('password'), ''], 'Passwords must match'),
})

export function ResetPassword() {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [searchParams, _] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    if (!searchParams.get('account') || !searchParams.get('reset_token')) {
      onError()
    }
  }, [])
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      const params = {
        email: searchParams.get('account'),
        password: values.password,
        token: searchParams.get('reset_token'),
      }
      setTimeout(() => {
        resetPassword(params)
          .then(async (res) => {
            setHasErrors(false)
            setLoading(false)
            await Swal.fire(DEFAULT_SUCCESS_MODAL('Reset password successfully!'))
            navigate('/auth/login')
          })
          .catch(async (error) => {
            setHasErrors(true)
            setLoading(false)
            setSubmitting(false)
            setStatus('The login detail is incorrect')
            await Swal.fire(DEFAULT_ERROR_MODAL(error.response.data.message))
          })
      }, 1000)
    },
  })

  const onError = async () => {
    await Swal.fire(
      DEFAULT_ERROR_MODAL('Sorry, looks like there are some errors detected, please try again.')
    )
    navigate('/auth/login')
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        {/* begin::Title */}
        <h1 className='text-dark fw-bolder mb-3'>Reset Password</h1>
        {/* end::Title */}

        {/* begin::Link */}
        <div className='text-gray-500 fw-semibold fs-6'>
          Enter your password to reset your password.
        </div>
        {/* end::Link */}
      </div>

      {/* begin::Title */}
      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            Sorry, looks like there are some errors detected, please try again.
          </div>
        </div>
      )}

      {/* end::Title */}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>Password</label>
        <input
          type='password'
          placeholder=''
          className={clsx('form-control bg-transparent')}
          autoComplete='off'
          {...formik.getFieldProps('password')}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>Confirm Password</label>
        <input
          type='password'
          placeholder=''
          className={clsx('form-control bg-transparent')}
          autoComplete='off'
          {...formik.getFieldProps('confirmPassword')}
        />
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.confirmPassword}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button type='submit' id='kt_password_reset_submit' className='btn btn-primary me-4'>
          <span className='indicator-label'>Submit</span>
          {loading && (
            <span className='indicator-progress'>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            Cancel
          </button>
        </Link>
      </div>
      {/* end::Form group */}
    </form>
  )
}
